import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Layout from '../components/Layout';
import { bankaService } from '../services/bankaService';

const BankaForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    bankaKodu: 0,
    bankaAdi: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && id) {
      const load = async () => {
        try {
          setLoadingData(true);
          setError(null);
          const b = await bankaService.getById(id);
          setFormData({
            bankaKodu: b.bankaKodu,
            bankaAdi: b.bankaAdi,
            isActive: b.isActive,
          });
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : null;
          setError(message || "Banka yüklenirken bir hata oluştu");
        } finally {
          setLoadingData(false);
        }
      };
      load();
    }
  }, [isEdit, id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (isEdit && id) {
        await bankaService.update({
          id,
          bankaKodu: formData.bankaKodu,
          bankaAdi: formData.bankaAdi,
          isActive: formData.isActive,
        });
      } else {
        await bankaService.create({
          bankaKodu: formData.bankaKodu,
          bankaAdi: formData.bankaAdi,
        });
      }
      navigate('/estatement/bankalar');
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      setError(message || 'İşlem sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/estatement/bankalar');
  };

  if (loadingData) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box mb={3}>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Banka Düzenle' : 'Yeni Banka'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, maxWidth: 560 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Banka Kodu"
            type="number"
            value={formData.bankaKodu}
            onChange={(e) => setFormData((prev) => ({ ...prev, bankaKodu: parseInt(e.target.value, 10) || 0 }))}
            margin="normal"
            required
            inputProps={{ min: 0 }}
          />

          <TextField
            fullWidth
            label="Banka Adı"
            value={formData.bankaAdi}
            onChange={(e) => setFormData((prev) => ({ ...prev, bankaAdi: e.target.value }))}
            margin="normal"
            required
          />

          {isEdit && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Aktif"
              sx={{ mt: 2 }}
            />
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
            <Button type="button" variant="outlined" onClick={handleCancel}>
              İptal
            </Button>
          </Box>
        </form>
      </Paper>
    </Layout>
  );
};

export default BankaForm;
